// SPDX-License-Identifier: AGPL-3.0-or-later

import * as Modal from '@app/features/app/components/dialogs/Modal';
import RuntimeConfig from '@app/features/app/state/RuntimeConfig';
import {completePrivyReauth, type PrivyReauthChallenge, startPrivyReauth} from '@app/features/auth/privy/PrivyApi';
import Sudo from '@app/features/auth/state/AuthSudo';
import SudoPrompt from '@app/features/auth/state/SudoPrompt';
import SessionManager from '@app/features/platform/state/AuthSession';
import {Button} from '@app/features/ui/button/Button';
import {Input} from '@app/features/ui/components/form/FormInput';
import {usePrivy} from '@privy-io/react-auth';
import {observer} from 'mobx-react-lite';
import {useEffect, useRef, useState} from 'react';

export const PrivyReauthModal = observer(() => {
	const {ready, authenticated, getAccessToken} = usePrivy();
	const [challenge, setChallenge] = useState<PrivyReauthChallenge | null>(null);
	const [code, setCode] = useState('');
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const inFlight = useRef(false);
	const controller = useRef(new AbortController());
	const tokenAtOpen = useRef(SessionManager.token);
	const snapshot = useRef(RuntimeConfig.getSnapshot());
	useEffect(() => {
		const current = new AbortController();
		controller.current = current;
		return () => current.abort();
	}, []);
	const isCurrent = () =>
		!controller.current.signal.aborted && tokenAtOpen.current !== null && SessionManager.token === tokenAtOpen.current;
	const close = () => {
		setCode('');
		setChallenge(null);
		controller.current.abort();
		SudoPrompt.reject(new DOMException('User cancelled verification', 'AbortError'));
	};
	const submit = async (event: React.FormEvent) => {
		event.preventDefault();
		if (inFlight.current || SudoPrompt.isVerifying || !ready || !isCurrent()) return;
		inFlight.current = true;
		setBusy(true);
		setError(null);
		try {
			if (!challenge) {
				const accessToken = await getAccessToken();
				if (!accessToken || !isCurrent()) throw new Error('Authentication required');
				const result = await startPrivyReauth(snapshot.current, accessToken, controller.current.signal);
				if (isCurrent()) setChallenge(result);
			} else {
				const input = code.trim();
				setCode('');
				const result = await completePrivyReauth(
					snapshot.current,
					challenge.challenge_id,
					input,
					controller.current.signal,
				);
				if (!isCurrent()) return;
				const expiration = Date.parse(result.expires_at);
				if (!result.sudo_token || !Number.isFinite(expiration) || expiration <= Date.now())
					throw new Error('Verification expired');
				Sudo.setToken(result.sudo_token);
				setChallenge(null);
				SudoPrompt.submit({});
			}
		} catch {
			if (isCurrent()) {
				setCode('');
				setChallenge(null);
				setError('验证未完成或验证码已失效，请重新发送后再试。');
			}
		} finally {
			inFlight.current = false;
			if (isCurrent()) setBusy(false);
		}
	};
	return (
		<Modal.Root size="small" centered onClose={close} data-flx="auth.privy.reauth-modal">
			<form onSubmit={(event) => void submit(event)} aria-label="验证敏感操作" data-flx="auth.privy.reauth-form">
				<Modal.Header title="确认是你本人" onClose={close} data-flx="auth.privy.reauth-header" />
				<Modal.Content data-flx="auth.privy.reauth-content">
					<Modal.ContentLayout data-flx="auth.privy.reauth-layout">
						<Modal.Description data-flx="auth.privy.reauth-description">
							敏感操作需要重新验证账号原邮箱。本次验证不会重新登录 DAOI 或占用新设备席位。
						</Modal.Description>
						{!authenticated && (
							<p role="alert" data-flx="auth.privy.reauth-sdk-session">
								请先在「我的」钱包管理中恢复原邮箱登录，再重试此操作。
							</p>
						)}
						{challenge && (
							<>
								<p role="status" data-flx="auth.privy.reauth-sent">
									验证码已发送至 {challenge.masked_email}，5 分钟内有效。输错后需重新发送。
								</p>
								<Input
									label="邮箱验证码"
									value={code}
									onChange={(event) => setCode(event.target.value)}
									autoComplete="one-time-code"
									inputMode="numeric"
									autoFocus
									required
									disabled={busy || SudoPrompt.isVerifying}
									data-flx="auth.privy.reauth-code"
								/>
							</>
						)}
						{error && (
							<p role="alert" data-flx="auth.privy.reauth-error">
								{error}
							</p>
						)}
					</Modal.ContentLayout>
				</Modal.Content>
				<Modal.Footer data-flx="auth.privy.reauth-footer">
					<Button type="button" variant="secondary" onClick={close} data-flx="auth.privy.reauth-cancel">
						取消
					</Button>
					<Button
						type="submit"
						submitting={busy || SudoPrompt.isVerifying}
						disabled={!ready || !authenticated || busy || SudoPrompt.isVerifying || Boolean(challenge && !code.trim())}
						data-flx="auth.privy.reauth-submit"
					>
						{challenge ? '验证并继续' : '发送验证码'}
					</Button>
				</Modal.Footer>
			</form>
		</Modal.Root>
	);
});
